import styled, { css } from 'styled-components'

export const ModalFrame = styled.section<{ $wide?: boolean }>`
  width: ${({ $wide }) => ($wide ? 'min(860px, calc(100vw - 32px))' : 'min(720px, calc(100vw - 32px))')};
  max-height: min(88vh, 760px);
  overflow: auto;
  border-radius: 16px;
  color: ${({ theme }) => theme.colors.primary};

  @media (max-width: 640px) {
    width: calc(100vw - 20px);
    max-height: 92vh;
  }
`

export const ValidationFrame = styled.section`
  width: min(440px, calc(100vw - 32px));
  border-radius: 16px;
  color: ${({ theme }) => theme.colors.primary};
`

export const ValidationBody = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 34px 30px 30px;
  text-align: center;

  h2 {
    margin: 17px 0 8px;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 19px;
  }

  p {
    max-width: 350px;
    margin: 0 0 22px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 13px;
    line-height: 1.55;
  }
`

export const ValidationIcon = styled.span<{ $denied: boolean }>`
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: ${({ theme, $denied }) => ($denied ? theme.colors.danger : theme.colors.accentColor)};
  background: ${({ theme, $denied }) => ($denied ? `${theme.colors.danger}16` : `${theme.colors.accentColor}16`)};
`

export const ValidationSpinner = styled.span`
  width: 24px;
  height: 24px;
  margin: 16px 0 0;
  border: 3px solid ${({ theme }) => `${theme.colors.accentColor}30`};
  border-top-color: ${({ theme }) => theme.colors.accentColor};
  border-radius: 50%;
  animation: voice-permission-spin 0.75s linear infinite;

  @keyframes voice-permission-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const ModalHeader = styled.header`
  display: flex;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
`

export const HeaderIcon = styled.span`
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.accentColor};
  background: ${({ theme }) => `${theme.colors.accentColor}18`};
`

export const HeaderCopy = styled.div`
  min-width: 0;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
  }

  p {
    margin: 5px 0 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 13px;
    line-height: 1.5;
  }
`

export const ModalBody = styled.div`
  padding: 22px 24px;

  @media (max-width: 640px) {
    padding: 18px;
  }
`

export const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 22px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

export const Field = styled.label`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 600;
`

export const TextArea = styled.textarea`
  min-height: 250px;
  width: 100%;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
  border-radius: 14px;
  outline: none;
  padding: 14px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.background.secondary};
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.55;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accentColor};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.accentColor}1f`};
  }
`

export const Counter = styled.span`
  align-self: flex-end;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`

export const TemplateSearch = styled.div`
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
  border-radius: 11px;
  padding: 0 13px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: ${({ theme }) => theme.background.input};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.accentColor};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.accentColor}1f`};
  }

  input {
    min-width: 0;
    flex: 1;
    border: 0;
    outline: 0;
    color: ${({ theme }) => theme.colors.primary};
    background: transparent;
    font: inherit;
    font-size: 13px;
  }
`

export const TemplateList = styled.div`
  max-height: 360px;
  display: grid;
  gap: 9px;
  margin-top: 14px;
  overflow: auto;
`

export const TemplateOption = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.accentColor : theme.colors.borderColorPrimary)};
  border-radius: 12px;
  padding: 13px 14px;
  cursor: pointer;
  background: ${({ theme, $selected }) => ($selected ? `${theme.colors.accentColor}12` : theme.background.secondary)};

  input {
    margin: 4px 0 0;
    accent-color: ${({ theme }) => theme.colors.accentColor};
  }
`

export const TemplateCopy = styled.div`
  min-width: 0;
  flex: 1;

  > span {
    display: -webkit-box;
    margin-top: 6px;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
    line-height: 1.45;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
`

export const TemplateHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  strong {
    min-width: 0;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const CategoryBadge = styled.span<{ $category: string }>`
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 4px 7px;
  color: ${({ theme, $category }) =>
    $category.toUpperCase() === 'UTILITY' ? theme.colors.accentColor : theme.colors.warning};
  background: ${({ theme, $category }) =>
    $category.toUpperCase() === 'UTILITY' ? `${theme.colors.accentColor}18` : `${theme.colors.warning}18`};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
`

export const TemplateEmpty = styled.div`
  padding: 34px 16px;
  border: 1px dashed ${({ theme }) => theme.colors.borderColorPrimary};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  font-size: 13px;
`

export const SelectedTemplate = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  border-radius: 10px;
  padding: 10px 12px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.accentColor}10`};

  small {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  strong {
    font-size: 12px;
  }
`

export const Preview = styled.div`
  min-height: 250px;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
  border-radius: 18px;
  padding: 12px;
  background: #efeae2;
  box-shadow: inset 0 1px 5px rgba(17, 27, 33, 0.1);

  ${({ theme }) =>
    theme.name === 'dark' &&
    css`
      background: #0b141a;
    `}
`

export const MessageBubble = styled.div`
  max-width: 94%;
  align-self: flex-end;
  border-radius: 12px 4px 12px 12px;
  padding: 10px 12px;
  color: #111b21;
  background: #d9fdd3;
  box-shadow: 0 1px 2px rgba(17, 27, 33, 0.15);
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const BubbleMeta = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  margin-top: 4px;
  color: rgba(17, 27, 33, 0.48);
  font-size: 9px;
`

export const PermissionCard = styled.div`
  margin-top: 8px;
  overflow: hidden;
  border-radius: 12px;
  color: #fff;
  background: #202c33;
  box-shadow: 0 1px 3px rgba(17, 27, 33, 0.18);
`

export const PermissionCardBody = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px;

  > span {
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.2);
  }

  strong,
  small {
    display: block;
    line-height: 1.4;
  }

  strong {
    font-size: 13px;
  }

  small {
    margin-top: 3px;
    color: rgba(255, 255, 255, 0.62);
    font-size: 12px;
  }
`

export const PermissionAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px;
  color: #21c063;
  font-size: 13px;
  font-weight: 700;
`

export const ModalFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
  padding: 16px 24px;
  background: ${({ theme }) => theme.background.primary};

  @media (max-width: 520px) {
    padding: 14px 18px;
    > button {
      flex: 1;
    }
  }
`

export const Button = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid
    ${({ theme, $primary, $danger }) =>
      $primary ? theme.colors.accentColor : $danger ? theme.colors.danger : theme.colors.borderColorPrimary};
  border-radius: 10px;
  padding: 0 16px;
  cursor: pointer;
  color: ${({ theme, $primary, $danger }) =>
    $primary ? '#fff' : $danger ? theme.colors.danger : theme.colors.primary};
  background: ${({ theme, $primary }) => ($primary ? theme.colors.accentColor : theme.background.secondary)};
  font-size: 13px;
  font-weight: 600;
  transition: opacity 0.15s ease, transform 0.1s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme, $primary }) => ($primary ? theme.colors.accentHover : theme.colors.borderColorSecondary)};
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const Select = styled.select`
  width: 100%;
  height: 42px;
  border: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
  border-radius: 10px;
  outline: none;
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.background.secondary};
`

export const Section = styled.section`
  & + & {
    margin: 22px 0;
  }

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 14px;
  }

  > p {
    margin: 5px 0 14px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
    line-height: 1.5;
  }
`

export const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`

export const Option = styled.button<{ $selected: boolean }>`
  min-height: 120px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.accentColor : theme.colors.borderColorPrimary)};
  border-radius: 14px;
  padding: 15px;
  cursor: pointer;
  text-align: left;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme, $selected }) => ($selected ? `${theme.colors.accentColor}12` : theme.background.secondary)};
  box-shadow: ${({ theme, $selected }) => ($selected ? `0 8px 24px ${theme.colors.accentColor}16` : 'none')};

  > span:first-child {
    width: 38px;
    height: 38px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: 10px;
    color: ${({ theme, $selected }) => ($selected ? '#fff' : theme.colors.text.secondary)};
    background: ${({ theme, $selected }) => ($selected ? theme.colors.accentColor : theme.colors.borderColorSecondary)};
  }

  strong,
  small {
    display: block;
  }

  strong {
    font-size: 13px;
  }

  small {
    margin-top: 7px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
    line-height: 1.5;
  }
`

export const Status = styled.div<{ $ready?: boolean; $warning?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid
    ${({ theme, $ready, $warning }) =>
      $ready
        ? `${theme.colors.accentColor}35`
        : $warning
        ? `${theme.colors.danger}35`
        : theme.colors.borderColorPrimary};
  border-radius: 12px;
  padding: 13px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme, $ready, $warning }) =>
    $ready ? `${theme.colors.accentColor}10` : $warning ? `${theme.colors.danger}0d` : theme.background.primary};
  font-size: 12px;
  line-height: 1.5;
`

export const Empty = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.borderColorPrimary};
  border-radius: 12px;
  padding: 24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  font-size: 13px;
`
